import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertCircle, AlertTriangle, FileText } from 'lucide-react';
import type { ComplianceIssue } from '../../types/scanner';

interface CheckItemProps {
  issue: ComplianceIssue;
}

export const CheckItem: React.FC<CheckItemProps> = ({ issue }) => {
  const [expanded, setExpanded] = useState(false);

  const config = {
    critical: {
      icon: AlertCircle,
      bg: '#fee2e2',
      border: 'var(--color-error)',
      iconColor: 'var(--color-error)',
      badgeBg: '#fecaca',
      badgeText: '#991b1b',
    },
    warning: {
      icon: AlertTriangle,
      bg: '#fef9c3',
      border: '#eab308',
      iconColor: '#eab308',
      badgeBg: '#fef08a',
      badgeText: '#854d0e',
    },
  };

  const { icon: Icon, bg, border, iconColor, badgeBg, badgeText } = config[issue.severity];
  const hasDetails = issue.file || issue.remediation;

  return (
    <div style={{
      backgroundColor: bg,
      border: `2px solid ${border}`,
      padding: 'var(--space-3)',
    }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <Icon size={20} style={{ color: iconColor, flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              padding: '2px 8px',
              backgroundColor: badgeBg,
              color: badgeText,
              fontWeight: 600,
            }}>
              {issue.code}
            </span>
            <span style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 500,
            }}>
              {issue.message}
            </span>
          </div>
        </div>
        {hasDetails && (
          <button style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-1)',
          }}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {expanded && hasDetails && (
        <div style={{
          marginTop: 'var(--space-3)',
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          marginLeft: 'calc(20px + var(--space-3))',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}>
          {issue.file && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-muted)',
            }}>
              <FileText size={16} />
              <span>File: {issue.file}</span>
            </div>
          )}
          {issue.remediation && (
            <div style={{ fontSize: 'var(--text-sm)' }}>
              <span style={{ fontWeight: 600 }}>How to fix: </span>
              <span style={{ color: 'var(--color-muted)' }}>{issue.remediation}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckItem;
